import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import styles from "./Recommendation.module.css";
import { Products } from "../web-app";
import authService from "../../services/authService";
import { useHomeQuery } from "../../queries/useHomeQuery";

const RECOMMENDATION_API =
  "https://roles.vitaura.co.in/fashion/api/recommendations/hybrid";

const Recommendations = ({
  wishItems = [],
  isWishListPending = false,
}) => {
  const [recommendedIds, setRecommendedIds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // =========================================================
  // GET HOME PRODUCTS
  // =========================================================

  const { data: homeData } = useHomeQuery();

  // =========================================================
  // CREATE COMPLETE PRODUCT LIST FROM HOME API
  // =========================================================

  const allHomeProducts = useMemo(() => {
    if (!homeData) {
      return [];
    }

    const {
      top_offers = [],
      new_arrivals = [],
      trending_products = [],
      sections = [],
    } = homeData;

    const sectionProducts = sections.flatMap((section) =>
      Array.isArray(section?.products)
        ? section.products
        : []
    );

    const products = [
      ...top_offers,
      ...new_arrivals,
      ...trending_products,
      ...sectionProducts,
    ];

    // Remove duplicate products
    const uniqueProducts = [];

    const productIds = new Set();

    products.forEach((product) => {
      if (!product?.id) {
        return;
      }

      if (productIds.has(product.id)) {
        return;
      }

      productIds.add(product.id);
      uniqueProducts.push(product);
    });

    return uniqueProducts;
  }, [homeData]);

  // =========================================================
  // FETCH RECOMMENDATIONS
  // =========================================================

  useEffect(() => {
    const fetchRecommendations = async () => {
      try {
        setIsLoading(true);

        // =====================================================
        // CHECK LOGIN
        // =====================================================

        const isLoggedIn = authService.isLoggedIn();

        console.log(
          "Recommendation user logged in:",
          isLoggedIn
        );

        if (!isLoggedIn) {
          setRecommendedIds([]);
          return;
        }

        // =====================================================
        // CURRENT USER
        // =====================================================

        const currentUser =
          authService.getCurrentUser();

        console.log(
          "Recommendation current user:",
          currentUser
        );

        // =====================================================
        // GET TOKEN
        // =====================================================

        let token = null;

        // Try authService.getToken()
        if (
          typeof authService.getToken ===
          "function"
        ) {
          try {
            token =
              authService.getToken();
          } catch (error) {
            console.log(
              "Could not get token from authService."
            );
          }
        }

        // Try current user
        if (!token && currentUser) {
          token =
            currentUser?.token ||
            currentUser?.access_token ||
            currentUser?.accessToken ||
            currentUser?.authToken ||
            null;
        }

        // Try localStorage
        if (!token) {
          const tokenNames = [
            process.env
              .REACT_APP_AUTH_TOKEN_NAME,
            "authToken",
            "token",
            "access_token",
            "accessToken",
            "jwt",
            "JWT",
          ].filter(Boolean);

          for (const tokenName of tokenNames) {
            const storedToken =
              localStorage.getItem(
                tokenName
              );

            if (storedToken) {
              token = storedToken;

              console.log(
                "Recommendation token found in localStorage:",
                tokenName
              );

              break;
            }
          }
        }

        // Try sessionStorage
        if (!token) {
          const tokenNames = [
            process.env
              .REACT_APP_AUTH_TOKEN_NAME,
            "authToken",
            "token",
            "access_token",
            "accessToken",
            "jwt",
            "JWT",
          ].filter(Boolean);

          for (const tokenName of tokenNames) {
            const storedToken =
              sessionStorage.getItem(
                tokenName
              );

            if (storedToken) {
              token = storedToken;

              console.log(
                "Recommendation token found in sessionStorage:",
                tokenName
              );

              break;
            }
          }
        }

        // =====================================================
        // CLEAN TOKEN
        // =====================================================

        if (typeof token === "string") {
          token = token.trim();

          if (
            token
              .toLowerCase()
              .startsWith("bearer ")
          ) {
            token = token
              .substring(7)
              .trim();
          }
        }

        console.log(
          "Recommendation token:",
          token
            ? "Token exists"
            : "No token"
        );

        // =====================================================
        // TOKEN REQUIRED
        // =====================================================

        if (!token) {
          console.error(
            "Recommendation API requires JWT but no token was found."
          );

          setRecommendedIds([]);
          return;
        }

        // =====================================================
        // CALL RECOMMENDATION API
        // =====================================================

        const response =
          await axios.get(
            RECOMMENDATION_API,
            {
              headers: {
                Accept:
                  "application/json",
                Authorization:
                  `Bearer ${token}`,
              },
            }
          );

        console.log(
          "Recommendation API response:",
          response?.data
        );

        // =====================================================
        // GET RECOMMENDED PRODUCTS
        // =====================================================

        const recommendedProducts =
          response?.data
            ?.recommended_products || [];

        console.log(
          "Recommended products:",
          recommendedProducts
        );

        // =====================================================
        // GET ONLY PRODUCT IDS
        // =====================================================

        const ids =
          recommendedProducts
            .map((product) =>
              Number(product?.id)
            )
            .filter(
              (id) =>
                Number.isFinite(id) &&
                id > 0
            );

        console.log(
          "Recommended product IDs:",
          ids
        );

        setRecommendedIds(ids);
      } catch (error) {
        console.error(
          "Recommendation API error:",
          error
        );

        console.error(
          "Recommendation API response:",
          error?.response?.data
        );

        console.error(
          "Recommendation API status:",
          error?.response?.status
        );

        setRecommendedIds([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRecommendations();
  }, []);

  // =========================================================
  // MATCH RECOMMENDATION IDs WITH FULL HOME PRODUCTS
  // =========================================================

  const recommendationProducts = useMemo(() => {
    if (
      !Array.isArray(recommendedIds) ||
      recommendedIds.length === 0
    ) {
      return [];
    }

    if (
      !Array.isArray(allHomeProducts) ||
      allHomeProducts.length === 0
    ) {
      return [];
    }

    const products = recommendedIds
      .map((recommendedId) => {
        const product =
          allHomeProducts.find(
            (item) =>
              Number(item?.id) ===
              Number(recommendedId)
          );

        return product;
      })
      .filter(Boolean);

    // =======================================================
    // NORMALIZE PRODUCT DATA
    // =======================================================

    return products
      .map((product) => {
        const variants =
          Array.isArray(
            product?.variants
          )
            ? product.variants
            : [];

        const activeVariant =
          variants.find(
            (variant) =>
              variant?.status !== false
          );

        const price = Number(
          activeVariant?.price ??
            product?.price ??
            0
        );

        const offerPrice = Number(
          activeVariant?.offer_price ??
            product?.offer_price ??
            0
        );

        const quantity =
          variants.length > 0
            ? variants.reduce(
                (
                  total,
                  variant
                ) =>
                  total +
                  Number(
                    variant?.quantity ||
                      0
                  ),
                0
              )
            : Number(
                product?.qty || 0
              );

        return {
          ...product,

          id: product.id,

          qty:
            quantity > 0
              ? quantity
              : 1,

          price,

          offer_price:
            offerPrice,

          sku:
            activeVariant?.sku ||
            product?.sku ||
            `REC-${product.id}`,

          thumb_image:
            product?.thumb_image ||
            "",

          image:
            product?.image ||
            product?.thumb_image ||
            "",

          image_url:
            product?.image_url ||
            product?.thumb_image ||
            "",

          variants,
        };
      })
      .filter(
        (product) =>
          Number(product?.qty) > 0
      );
  }, [
    recommendedIds,
    allHomeProducts,
  ]);

  // =========================================================
  // LOADING
  // =========================================================

  if (isLoading) {
    return null;
  }

  // =========================================================
  // NO RECOMMENDATIONS
  // =========================================================

  if (
    !Array.isArray(
      recommendationProducts
    ) ||
    recommendationProducts.length === 0
  ) {
    return null;
  }

  // =========================================================
  // SAME PRODUCTS COMPONENT
  // =========================================================

  return (
    <section
      className={
        styles.recommendationSection
      }
    >
      <Products
        title="Recommended For You"
        products={
          recommendationProducts
        }
        wishItems={wishItems}
        isWishListPending={
          isWishListPending
        }
        variant="slider"
      />
    </section>
  );
};

export default Recommendations;